import { computed } from 'mobx'
import { DocumentSupportingItemInput } from '@/features/jobs/info-gathering/documents/DocumentSupportingItemInput'
import { type JobDocumentsViewModel } from '@/features/jobs/details/JobDocumentsViewModel'
import { type JobCapturedInformationViewModel } from '@/features/jobs/details/JobCapturedInformationViewModel'
import { displayTitle } from '@/features/jobs/details/display-title'
import { type Id } from '@/infra/id'

export interface ChecklistRow {
  key: Id
  name: string
  done: boolean
  via: string
}

/**
 * Summarises the job's documents for the overview pane.
 */
export class OverviewPaneViewModel {
  constructor(
    private readonly _documentsVm: JobDocumentsViewModel,
    private readonly _capturedInformationVm: JobCapturedInformationViewModel,
  ) {}

  /**
   * Whether the checklist is still loading with nothing to show yet. Without
   * requested items it lists the documents, so it waits on those too.
   */
  @computed
  get isChecklistFetchingInitial() {
    if (this._capturedInformationVm.isFetchingInitial) {
      return true
    }

    return (
      !this._documentsVm.hasSupportingItems &&
      this._documentsVm.isFetchingInitial
    )
  }

  /**
   * One row per thing the firm asked for, what's still missing first. Jobs
   * without a checklist list what has arrived by document type instead.
   */
  @computed
  get checklistRows(): ChecklistRow[] {
    const requested: ChecklistRow[] = []
    this._documentsVm.supportingItems.forEach((section) =>
      section.requestedSupportingItems.forEach((item) => {
        const documents =
          item.input instanceof DocumentSupportingItemInput
            ? item.input.documents
            : []
        requested.push({
          key: item.id,
          name: item.name,
          done: item.completed,
          via: filesLabel(documents.map((d) => d.title)),
        })
      }),
    )
    if (requested.length > 0) {
      return requested.sort((a, b) => Number(a.done) - Number(b.done))
    }

    const byType = new Map<string, string[]>()
    this._documentsVm.documents.forEach((doc) => {
      const type = doc.documentType?.name ?? 'Other'
      byType.set(type, [...(byType.get(type) ?? []), doc.title])
    })
    return Array.from(byType.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([type, titles]) => ({
        key: type,
        name: type,
        done: true,
        via: filesLabel(titles),
      }))
  }

  /**
   * Whether the job has any documents.
   */
  @computed
  get hasDocuments() {
    return this._documentsVm.documents.length > 0
  }

  /**
   * Whether there are recently added documents to list.
   */
  @computed
  get hasRecentDocuments() {
    return this.recentDocumentVms.length > 0
  }

  /**
   * View models for the loaded documents, most recently added first.
   */
  @computed
  get recentDocumentVms() {
    return this._documentsVm.listVm.documentVms
      .slice()
      .sort(
        (a, b) =>
          b.document.dateCreated.getTime() - a.document.dateCreated.getTime(),
      )
  }
}

function filesLabel(titles: string[]) {
  if (titles.length === 0) {
    return ''
  }
  return titles.length === 1
    ? displayTitle(titles[0])
    : `${titles.length} files`
}
